import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Chip,
  Icon,
  Textarea,
  Toast,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { formatDateTime } from "@/utils";
import { useApprovalRequest } from "@/hooks/useApprovalRequest";
import { useLock, useUnlock, useUpdateContentOutput } from "@/hooks/useLock";
import { useApprove, useReject, useSuspend } from "@/hooks/useApprovalActions";
import { usePermissions } from "@/hooks/usePermissions";
import { pipelineService, type UnlinkTestimonialType } from "@/api/pipeline.service";
import { TESTIMONIALS_UNLINK_LAWYER_PERMISSION } from "@/constants/permissions";
import { useNavigationGuard } from "@/context/NavigationGuardContext";
import {
  getDisplayContent,
  getFirmName,
  getLanguageFlag,
  getLangDisplay,
  mapStatus,
  parseTransformations,
  type ContentStatus,
} from "./utils";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { UnsavedChangesModal } from "@/components/Modal/UnsavedChangesModal";
import styles from "./ContentInboxDetail.module.css";
import { PageHeader } from "@/components/PageHeader";
import { ChangeHistoryLink } from "@/components/ChangeHistoryLink";
import { LinkContactModal } from "./components/LinkContactModal";
import { LawyerContactLink } from "./components/LawyerContactLink";
import { UnlinkLawyerButton } from "./components/UnlinkLawyerButton";
import { DetailDecisionButtons } from "./components/DetailDecisionButtons";

const STATUS_CHIPS: Partial<Record<ContentStatus, { label: string; className: string }>> = {
  NEW: { label: "▲ New", className: styles.statusBadgeNew },
  APPROVED: { label: "✓ Approved", className: styles.statusBadgeApproved },
  REJECTED: { label: "✗ Rejected", className: styles.statusBadgeRejected },
  SUSPENDED: { label: "⏸ Suspended", className: styles.statusBadgeSuspended },
  WITHDRAWN: { label: "↩ Withdrawn", className: styles.statusBadgeWithdrawn },
};

const UNLOCK_FAILED = "Failed to unlock record. It will unlock automatically after some time.";

function ContentInboxDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = `/research/content-inbox${location.state?.returnSearch ?? ""}`;
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch: refetchApprovalRequest } = useApprovalRequest(id);
  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [linkingContact, setLinkingContact] = useState(false);
  const [discardChangesOpen, setDiscardChangesOpen] = useState(false);
  const [lockError, setLockError] = useState<Error | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const { mutate: lock } = useLock();
  const { mutate: unlock, loading: unlocking } = useUnlock();
  const { mutate: approve, loading: approving } = useApprove();
  const { mutate: reject, loading: rejecting } = useReject();
  const { mutate: suspend, loading: suspending } = useSuspend();
  const { mutate: saveContentOutput, loading: savingContent } = useUpdateContentOutput();
  const { user, hasPermission } = usePermissions();
  const { setDirty: setNavDirty } = useNavigationGuard();
  const currentUserId = user?.id;

  const approvalRequest = data?.approvalRequest;
  const storedText = approvalRequest ? getDisplayContent(approvalRequest) : "";
  const outputText = editedContent ?? storedText;
  const isOutputBlank = outputText.trim() === "";
  const isDirty = editedContent !== null && editedContent !== storedText;

  useEffect(() => {
    setEditedContent(null);
  }, [approvalRequest]);

  useEffect(() => {
    if (approvalRequest && id) {
      const now = new Date();
      const isLockedByMe =
        approvalRequest.lockedTo &&
        approvalRequest.lockedBy &&
        new Date(approvalRequest.lockedTo) > now &&
        approvalRequest.lockedBy === currentUserId;

      const isLockedByOther =
        approvalRequest.lockedTo &&
        approvalRequest.lockedBy &&
        new Date(approvalRequest.lockedTo) > now &&
        approvalRequest.lockedBy !== currentUserId;

      if (isLockedByMe) {
        setIsLocked(true);
        setLockError(null);
        return;
      }

      if (isLockedByOther) {
        const lockedToDate = approvalRequest.lockedTo
          ? new Date(approvalRequest.lockedTo)
          : new Date();
        const error = new Error(
          `Record is already locked by another user until ${lockedToDate.toISOString()}`,
        ) as Error & { status?: number };
        error.status = 409;
        setLockError(error);
        setIsLocked(false);
        return;
      }

      if (approvalRequest.status === "NEW") {
        lock(id)
          .then(() => {
            setIsLocked(true);
            setLockError(null);
          })
          .catch((error: Error) => {
            const err = error instanceof Error ? error : new Error(String(error));
            setLockError(err);
            const status = (err as Error & { status?: number }).status;
            if (status === 409) {
              setIsLocked(false);
            }
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalRequest?.id, id, currentUserId]);

  // Sidebar links go through the app-wide guard; components/Layout asks first while this is set.
  useEffect(() => {
    setNavDirty(isDirty);
    return () => setNavDirty(false);
  }, [isDirty, setNavDirty]);

  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (isDirty) e.preventDefault();
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  if (loading) {
    return <LoadingState message="Loading content..." />;
  }

  if (error) {
    const status = error && (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;

    if (isAuthError) {
      return <UnauthorizedView />;
    }

    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Failed to load content"
          message={error?.message || "Content not found"}
          action={{
            label: "Back to List",
            onClick: () => navigate(returnPath),
          }}
        />
      </div>
    );
  }

  if (!approvalRequest) {
    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Content not found"
          message="The requested content item could not be found."
          action={{
            label: "Back to List",
            onClick: () => navigate(returnPath),
          }}
        />
      </div>
    );
  }

  const status = mapStatus(approvalRequest.status);
  const statusChip = STATUS_CHIPS[status];
  const isEditableStatus = status === "NEW";
  const transformations = parseTransformations(approvalRequest.transformations);
  const practicePath = approvalRequest.practiceAreaName
    ? `${approvalRequest.practiceGroupName ? `${approvalRequest.practiceGroupName} → ` : ""}${approvalRequest.practiceAreaName}`
    : approvalRequest.practiceGroupName || "";

  const isLockedByOther = lockError
    ? (lockError as Error & { status?: number }).status === 409
    : !!(
        approvalRequest?.lockedTo &&
        approvalRequest?.lockedBy &&
        new Date(approvalRequest.lockedTo) > new Date() &&
        approvalRequest.lockedBy !== currentUserId
      );

  const formatLockErrorMessage = (message: string): string => {
    const dateMatch = message.match(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z)/);
    if (dateMatch) {
      const isoDate = dateMatch[1];
      const formattedDate = formatDateTime(isoDate);
      return message.replace(isoDate, formattedDate);
    }
    return message;
  };

  const canUnlinkLawyer =
    hasPermission(TESTIMONIALS_UNLINK_LAWYER_PERMISSION) &&
    (approvalRequest.contactId != null || !!approvalRequest.lawyerName);
  const busy = approving || rejecting || suspending || savingContent;

  const lockErrorMessage = isLockedByOther
    ? formatLockErrorMessage(lockError?.message || "This record is locked by another user")
    : null;

  const leaveToList = async () => {
    if (id && isLocked && !isLockedByOther) {
      try {
        await unlock(id);
      } catch (_error) {
        setToast({ message: UNLOCK_FAILED, type: "error" });
      }
    }
    navigate(returnPath);
  };

  const handleBack = () => {
    if (isDirty) {
      setDiscardChangesOpen(true);
    } else {
      void leaveToList();
    }
  };

  const handleSaveChanges = async () => {
    if (!id || isOutputBlank) return;
    try {
      await saveContentOutput(id, outputText);
      setIsLocked(false);
      navigate(returnPath);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save changes";
      setToast({
        message: `Request failed: ${errorMessage}`,
        type: "error",
      });
    }
  };

  const handleApprove = async () => {
    if (!id || isOutputBlank) return;
    // A successful content-output save clears the lock server-side (approval-api
    // updateContentOutput), exactly like handleSaveChanges relies on, so after a save
    // there is nothing left to unlock.
    let holdsLock = isLocked && !isLockedByOther;
    if (isDirty) {
      try {
        await saveContentOutput(id, outputText);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to save changes";
        setToast({
          message: `Changes not saved, request not approved: ${errorMessage}`,
          type: "error",
        });
        return;
      }
      setIsLocked(false);
      holdsLock = false;
    }
    try {
      await approve(id);
      await refetchApprovalRequest();
      if (holdsLock) {
        try {
          await unlock(id);
        } catch (_error) {
          setToast({ message: UNLOCK_FAILED, type: "error" });
        }
      }
      navigate(returnPath);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to approve request";
      setToast({
        message: `Request failed: ${errorMessage}`,
        type: "error",
      });
    }
  };

  const handleDecide = async (action: "reject" | "suspend") => {
    if (!id) return;
    try {
      await (action === "reject" ? reject(id) : suspend(id));
      await refetchApprovalRequest();
      if (isLocked && !isLockedByOther) {
        try {
          await unlock(id);
        } catch (_error) {
          setToast({ message: UNLOCK_FAILED, type: "error" });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to ${action} request`;
      setToast({
        message: `Request failed: ${errorMessage}`,
        type: "error",
      });
    }
  };

  const handleUnlinkLawyer = async (testimonialType: UnlinkTestimonialType | undefined) => {
    try {
      await pipelineService.unlinkLawyerFromProcessingItem(
        approvalRequest.externalId,
        testimonialType,
      );
      await refetchApprovalRequest();
      setToast({ message: "Lawyer removed from testimonial", type: "success" });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to remove lawyer";
      setToast({
        message: `Request failed: ${errorMessage}`,
        type: "error",
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Approval Request">
          <div style={{ display: "flex", gap: "12px" }}>
            <ChangeHistoryLink source="approval-requests" entityId={approvalRequest.id} />
            <Button variant="outlined" color="default" onClick={handleBack} disabled={unlocking}>
              <Icon icon="arrow-left" size="small" />
              Back
            </Button>
          </div>
        </PageHeader>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Publication</span>
              <span className={styles.infoValue}>{approvalRequest.publicationName || "—"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Edition</span>
              <span className={styles.infoValue}>{approvalRequest.edition || "—"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Region</span>
              <span className={styles.infoValue}>{approvalRequest.regionName || "—"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Practice Area</span>
              <span className={styles.infoValue}>{practicePath || "—"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Firm</span>
              <span className={styles.infoValue}>{getFirmName(approvalRequest) || "—"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Referee</span>
              <span className={styles.infoValue}>—</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Lawyer</span>
              <span className={styles.infoValue}>
                <LawyerContactLink
                  lawyerName={approvalRequest.lawyerName}
                  contactId={approvalRequest.contactId}
                  onLink={() => setLinkingContact(true)}
                />
                {canUnlinkLawyer && (
                  <UnlinkLawyerButton
                    currentType={approvalRequest.contentType}
                    disabled={busy}
                    onConfirm={handleUnlinkLawyer}
                  />
                )}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Type</span>
              <span className={styles.infoValue}>
                {approvalRequest.contentType
                  ? approvalRequest.contentType.replace("_", " ").toUpperCase()
                  : "—"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Status</span>
              <div className={styles.statusContainer}>
                {statusChip && (
                  <Chip size="small" variant="filled" className={statusChip.className}>
                    {statusChip.label}
                  </Chip>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.contentColumns}>
          <div className={styles.section}>
            <div className={styles.contentHeader}>
              <h2 className={styles.sectionTitle}>Original input (Read-only)</h2>
              {approvalRequest.langInput && (
                <Chip size="small" variant="outlined" className={styles.languageChip}>
                  {getLanguageFlag(approvalRequest.langInput)}{" "}
                  {getLangDisplay(approvalRequest.langInput).toUpperCase()}
                </Chip>
              )}
            </div>
            <Textarea
              value={approvalRequest.contentInput || ""}
              readOnly
              className={styles.readOnlyTextarea}
              rows={6}
            />
          </div>

          <div className={styles.section}>
            <div className={styles.contentHeader}>
              <h2 className={styles.sectionTitle}>Transformed input (Read-only)</h2>
              {approvalRequest.langInput && (
                <Chip size="small" variant="outlined" className={styles.languageChip}>
                  {getLanguageFlag(approvalRequest.langInput)}{" "}
                  {getLangDisplay(approvalRequest.langInput).toUpperCase()}
                </Chip>
              )}
              {transformations.length > 0 && (
                <div className={styles.processTags}>
                  {transformations.map((tag, index) => (
                    <Chip key={index} size="small" variant="outlined" className={styles.processTag}>
                      {tag}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
            <Textarea
              value={approvalRequest.contentTransformed || approvalRequest.contentInput || ""}
              readOnly
              className={styles.readOnlyTextarea}
              rows={6}
            />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.contentHeader}>
            <h2 className={styles.sectionTitle}>Output version (Editable)</h2>
            {approvalRequest.langOutput && (
              <Chip size="small" variant="outlined" className={styles.languageChip}>
                {getLanguageFlag(approvalRequest.langOutput)}{" "}
                {getLangDisplay(approvalRequest.langOutput).toUpperCase()}
              </Chip>
            )}
          </div>
          <Textarea
            value={outputText}
            onChange={e => setEditedContent(e.target.value)}
            className={styles.editableTextarea}
            rows={6}
            disabled={isLockedByOther || !isEditableStatus}
          />
        </div>
      </div>

      {lockErrorMessage && <div className={styles.lockError}>{lockErrorMessage}</div>}

      <DetailDecisionButtons
        status={status}
        lockedByOther={isLockedByOther}
        busy={busy}
        outputBlank={isOutputBlank}
        onSave={handleSaveChanges}
        onApprove={handleApprove}
        onReject={() => handleDecide("reject")}
        onSuspend={() => handleDecide("suspend")}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}

      {linkingContact && approvalRequest && (
        <LinkContactModal
          item={approvalRequest}
          onClose={() => setLinkingContact(false)}
          onLinked={() => {
            setLinkingContact(false);
            refetchApprovalRequest();
          }}
        />
      )}

      {discardChangesOpen && (
        <UnsavedChangesModal
          onClose={() => setDiscardChangesOpen(false)}
          onConfirm={() => {
            setDiscardChangesOpen(false);
            void leaveToList();
          }}
        />
      )}
    </div>
  );
}

export default ContentInboxDetail;
